import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NavigationModal from "../../layouts/NavigationModal";
import { gallery_styles } from "../../utils/gallery.js";
import { axios_instance } from "../../api/axios_instance";
import PaymentSummary from "./paymentsummary/PaymentSummary";
import DealerLedger from "./dealerledger/DealerLedger";
import RTOSummary from "./rtosummary/RTOSummary";
import HoldCaseModal from "./HoldCaseModal";
import DetailsSideBar from "./sidebar/DetailsSideBar";
import RemarkPopper from "./Remarks/RemarkPopper";
import ImageContainer from "../../layouts/ImageContainer";
import DetailForm from "./DetailsForm/DetailForm.jsx";
import HiddenFields from "./HiddenFields";
import { ToastContainer, toast } from "react-toastify";
import ContainerLoading from "../../layouts/ContainerLoading.jsx";
import {
  decryptRequest,
  encryptRequest,
} from "../../utils/encryptdecryptrequest.js";
import Header from "../../layouts/Header.jsx";
import ConfirmBox from "../../layouts/ConfirmBox.jsx";
import LoadingModal from "../../layouts/LoadingModal.jsx";
import {
  DetailFormContext,
  DetailPageLoadingModalContext,
} from "../../ContextAPI/context.js";
import { CSSTransition } from "react-transition-group";

const Details = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const lead_id = atob(searchParams.get("lead_id"));
  const user = useSelector((state) => state.user);

  const initialState = {
    docType: [0, 0],
    documentList: [],
    user,
    caseDetails: {},
    showNavigationModal: false,
    showHoldCaseModal: false,
    showReleaseConfirmBox: false,
    isRemarksOpen: false,
    flip: false,
    selectedPanel: 0,
    togglePanel: true,
    stepSelected: "",
    stepObj: {},
    panels: [],
    rotation1: 0,
    rotation2: 0,
    canScroll: [false, false],
    holdCaseReasons: [],
    holdStatus: -1,
    holdCaseDetails: {},
    statuses: [],
    substatuses: [],
    controlsystem: {},
    payleadData: {},
    RCLimit: {},
  };

  const [detailsState, setDetailsState] = useState(initialState);
  const [rbacConfig, setRbacConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [detailFormLoading, setDetailFormLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [isMoneyReturnToBank, setIsMoneyReturnToBank] = useState(false);
  const [fetchHoldCaseRemarksState, setFetchHoldCaseRemarksState] =
    useState(false);
  const [paymentSummaryLoading, setPaymentSummaryLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [detailFormButtonClicked, setDetailFormButtonClicked] = useState(false);
  const dispatch = useDispatch();

  const resetScroll = useCallback(
    (idx) => {
      const canScroll = detailsState.canScroll;
      canScroll[idx] = false;
      return canScroll;
    },
    [detailsState.canScroll]
  );

  const fetchDocumentURL = async (docType, idx) => {
    try {
      setDetailsState((prevState) => ({
        ...prevState,
        docType,
        [`rotation${idx + 1}`]: 0,
        canScroll: resetScroll(idx),
      }));
    } catch (error) {}
  };

  const fetchImageData = useCallback(
    async (resData) => {
      try {
        let firstIdx = -1,
          secondIdx = -1;
        if (resData.shuffleGalleryDocs.gallery_one.length) {
          detailsState.documentList.find((doc, index) => {
            const cond =
              +doc.document_id ===
              +resData.shuffleGalleryDocs.gallery_one[0].id;
            if (cond) firstIdx = index;
            return cond;
          });
        }

        if (resData.shuffleGalleryDocs.gallery_two.length) {
          detailsState.documentList.find((doc, index) => {
            const cond =
              +doc.document_id ===
              +resData.shuffleGalleryDocs.gallery_two[0].id;
            if (cond) secondIdx = index;
            return cond;
          });
        }
        const docType = [
          firstIdx === -1 ? detailsState.docType[0] : firstIdx,
          secondIdx === -1 ? detailsState.docType[1] : secondIdx,
        ];
        setImageLoading(true);
        await fetchDocumentURL(docType, 0);
        await fetchDocumentURL(docType, 1);
        setImageLoading(false);
      } catch (error) {}
    },
    [detailsState.docType, detailsState.documentList, fetchDocumentURL]
  );

  const formFieldValueFunc = useCallback(
    async (shouldImageLoad = false) => {
      setRbacConfig((prevState) => ({
        ...prevState,
        caseDetails: {},
      }));
      try {
        let reqData = encryptRequest({
          lead_id,
          stepSelected: detailsState.stepSelected,
          nextStep: detailsState.stepObj.nextStep,
        });
        let response = await axios_instance({
          url: "/rbac/getFormFields",
          method: "POST",
          data: { reqData },
        });

        if (response.status === 200) {
          response = decryptRequest(response.data.resData);
          if (shouldImageLoad) {
            await fetchImageData(response);
          }

          setRbacConfig((prevState) => ({
            ...prevState,
            ...response,
          }));
        }

        if (detailsState.stepSelected === "payment_tranche") {
          reqData = encryptRequest({ caseDetails: detailsState.caseDetails });
          let controlsystem = await axios_instance({
            url: "/dts/getControlTypeSystem",
            method: "POST",
            data: { reqData },
          });
          controlsystem = decryptRequest(controlsystem.data.resData);

          reqData = encryptRequest({
            caseDetails: {
              id: detailsState.caseDetails.id,
              dts_pay_count: detailsState.caseDetails.dts_pay_count,
              net_disbursement_amount:
                detailsState.caseDetails.net_disbursement_amount,
              lead_disbursed_amt: detailsState.caseDetails.lead_disbursed_amt,
              payable_ac_number:
                detailsState?.caseDetails?.paymentDetails[
                  detailsState?.caseDetails?.dts_pay_count - 1
                ]?.payable_ac_number,
            },
            control_type: controlsystem.result.type,
          });
          let payleadDataResp = await axios_instance({
            url: "/dts/getPayleadLimitUserAccess",
            method: "POST",
            data: { reqData },
          });
          payleadDataResp = decryptRequest(payleadDataResp.data.resData);
          setDetailsState((prevState) => ({
            ...prevState,

            controlsystem: controlsystem.result,
            payleadData: payleadDataResp.result,
          }));
        }
      } catch (error) {
        console.log(error);
      }
      setDetailFormLoading(false);
    },
    [
      detailsState.caseDetails,
      detailsState.stepObj.nextStep,
      detailsState.stepSelected,
    ]
  );

  const prevStepSelectedRef = useRef(detailsState.stepSelected);
  useEffect(() => {
    if (Object.keys(detailsState.caseDetails).length > 2) {
      setDetailFormLoading(true);
      formFieldValueFunc(
        detailsState.stepSelected !== prevStepSelectedRef.current
      );
    }
    prevStepSelectedRef.current = detailsState.stepSelected;
  }, [detailsState.caseDetails, detailsState.stepSelected, formFieldValueFunc]);

  const fetchPaymentDetails = async () => {
    setDetailFormLoading(true);
    try {
      let reqData = encryptRequest({ lead_id });
      let casedetails = await axios_instance({
        url: "/dts/getDTSCaseDetail",
        method: "POST",
        data: { reqData },
      });
      casedetails = decryptRequest(casedetails.data.resData);

      reqData = encryptRequest({
        caseDetails: casedetails.result,
      });
      let paymentdetails = await axios_instance({
        url: "/dts/getPaymentDetail",
        method: "POST",
        data: { reqData },
      });

      paymentdetails = decryptRequest(paymentdetails.data.resData);

      setDetailsState((prevState) => ({
        ...prevState,
        caseDetails: {
          ...casedetails.result,
          paymentDetails: paymentdetails.payment_details,
        },
      }));
    } catch (error) {
    } finally {
      setDetailFormLoading(false);
    }
  };

  const fetchDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      let reqData = encryptRequest({ lead_id });
      let casedetails = await axios_instance({
        url: "/dts/getDTSCaseDetail",
        method: "POST",
        data: { reqData },
      });

      casedetails = decryptRequest(casedetails.data.resData);
      const caseDetailsResult = casedetails.result;

      reqData = encryptRequest({ caseDetails: caseDetailsResult });

      let paymentdetails = await axios_instance({
        url: "/dts/getPaymentDetail",
        method: "POST",
        data: { reqData },
      });
      paymentdetails = decryptRequest(paymentdetails.data.resData);

      reqData = encryptRequest({
        loan_id: lead_id,
        isPaymentRequested: "",
        isPrePaymentRaised: "",
        dis_id: "",
        source: "",
      });
      let docs_response = await axios_instance({
        url: "/dts/getImageList",
        method: "POST",
        data: { reqData },
      });
      docs_response = decryptRequest(docs_response.data.resData);

      const holdStatus = caseDetailsResult.holdStatus;

      reqData = encryptRequest({ caseDetails: caseDetailsResult });
      let controlsystem = await axios_instance({
        url: "/dts/getControlTypeSystem",
        method: "POST",
        data: { reqData },
      });
      controlsystem = decryptRequest(controlsystem.data.resData);

      reqData = encryptRequest({
        caseDetails: {
          id: caseDetailsResult.id,
          dts_pay_count: caseDetailsResult.dts_pay_count,
          net_disbursement_amount: caseDetailsResult.net_disbursement_amount,
          lead_disbursed_amt: caseDetailsResult.lead_disbursed_amt,
          payable_ac_number:
            paymentdetails?.payment_details[
              caseDetailsResult?.dts_pay_count - 1
            ]?.payable_ac_number,
        },
        control_type: controlsystem.result.type,
      });

      let payleadDataResp = await axios_instance({
        url: "/dts/getPayleadLimitUserAccess",
        method: "POST",
        data: { reqData },
      });
      payleadDataResp = decryptRequest(payleadDataResp.data.resData);

      reqData = encryptRequest({ caseDetails: caseDetailsResult });
      let res = await axios_instance({
        url: "/dts/getRCLimitDealers",
        method: "POST",
        data: { reqData },
      });

      res = decryptRequest(res.data.resData);

      if (!detailsState.panels.length) {
        setDetailsState((prevState) => ({
          ...prevState,
          selectedPanel: caseDetailsResult.leftPanelData.selectedPanel,
          stepSelected: caseDetailsResult.leftPanelData.stepSelected,
          stepObj: caseDetailsResult.leftPanelData.stepObj,
          panels: caseDetailsResult.leftPanelData.panels,
        }));
      }

      const response_data = caseDetailsResult.response_data
        ? caseDetailsResult.response_data
        : JSON.stringify(controlsystem.result);

      setDetailsState((prevState) => ({
        ...prevState,
        documentList: docs_response.documents,
        caseDetails: {
          ...caseDetailsResult,
          paymentDetails: paymentdetails.payment_details,
          response_data,
        },
        holdCaseReasons: caseDetailsResult.holdCaseReasons,
        holdStatus,
        holdCaseDetails: caseDetailsResult.holdCaseDetails,
        statuses: caseDetailsResult.statuses,
        substatuses: caseDetailsResult.subStatuses,
        controlsystem: controlsystem.result,
        payleadData: payleadDataResp.result,
        RCLimit: res?.result ? res.result : {},
      }));
    } catch (error) {
      if (error.response && error.response.status !== 500) {
        alert("Your login has been expired, Please login again to continue.");
        dispatch({ type: "remove_user" });
        localStorage.removeItem("token");
        setDetailsState((prevState) => ({ ...prevState, user: null }));
      }
    }
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    fetchDetails();
  }, [lead_id, fetchDetails]);

  const onDocumentChange = async (e, idx) => {
    const docType = detailsState.docType;
    docType[idx] = parseInt(e.target.value);
    fetchDocumentURL(docType, idx);
  };

  const prevDoc = (idx) => {
    const docType = detailsState.docType;
    const len = detailsState.documentList.length;
    docType[idx] = (docType[idx] - 1 + len) % len;
    fetchDocumentURL(docType, idx);
  };

  const nextDoc = (idx) => {
    const docType = detailsState.docType;
    const len = detailsState.documentList.length;
    docType[idx] = (docType[idx] + 1) % len;
    fetchDocumentURL(docType, idx);
  };

  const rotate = (id) => {
    if (detailsState[`rotation${id}`] === 3) {
      setDetailsState({ ...detailsState, [`rotation${id}`]: 0 });
    } else {
      setDetailsState({
        ...detailsState,
        [`rotation${id}`]: detailsState[`rotation${id}`] + 1,
      });
    }
  };

  const enableScroll = (idx) => {
    const canScroll = detailsState.canScroll;
    canScroll[idx] = true;
    setDetailsState({ ...detailsState, canScroll });
  };

  const toggleRemarkPopper = (e) => {
    setDetailsState({
      ...detailsState,
      isRemarksOpen: !detailsState.isRemarksOpen,
    });
  };

  const handlePanelClick = (e) => {
    const panelId = parseInt(e.target.id);
    const toggle =
      panelId === detailsState.selectedPanel ? !detailsState.togglePanel : true;

    setDetailsState({
      ...detailsState,
      selectedPanel: panelId,
      togglePanel: toggle,
    });
  };

  const handleStepClick = (stepSelected, stepObj) => {
    if (detailsState.stepSelected === stepSelected) {
      return;
    }
    setDetailFormLoading(true);
    setDetailsState({
      ...detailsState,
      stepSelected,
      stepObj,
    });
  };

  const handleNextStep = () => {
    setDetailFormLoading(true);
    const curStepObj = detailsState.stepObj;
    const nextPanelId = curStepObj.nextPanelId;
    const nextStepObj =
      detailsState.panels[nextPanelId].steps[curStepObj.nextStep];
    setDetailsState((prevState) => ({
      ...prevState,
      stepSelected: curStepObj.nextStep,
      stepObj: nextStepObj,
      selectedPanel: nextPanelId,
      togglePanel: true,
    }));
  };

  const handleReleaseConfirm = () => {
    setDetailsState({ ...detailsState, showReleaseConfirmBox: true });
  };

  const releaseCase = async () => {
    try {
      setShowLoadingModal(true);
      const result = await axios_instance({
        url: "/dts/releaseHoldCase",
        method: "POST",
        data: {
          ref_id: lead_id,
          release_by: detailsState.user.id,
        },
      });
      if (result.status === 200) {
        toast(
          <>
            <span className="glyphicon glyphicon-ok-sign"></span>
            Case has been released successfully
          </>
        );
        changeHoldStatus(1);
      } else {
        toast.dismiss();
        return toast.error("Something went wrong.", {
          theme: "colored",
        });
      }
    } catch (error) {
      toast.dismiss();
      return toast.error("Some error occurred while releasing the case", {
        theme: "colored",
      });
    } finally {
      setShowLoadingModal(false);
    }
  };

  const changeHoldStatus = (holdStatus) => {
    if (+holdStatus === 2) setFetchHoldCaseRemarksState(true);

    setDetailsState((prevState) => ({
      ...prevState,
      holdStatus,
      showHoldCaseModal: false,
    }));
  };

  const changeMoneyReturnToBank = (changedValue) => {
    setIsMoneyReturnToBank(changedValue);
  };

  const fetchGallery = async () => {
    try {
      const reqData = encryptRequest({
        loan_id: lead_id,
        isPaymentRequested: "",
        isPrePaymentRaised: "",
        dis_id: "",
        source: "",
      });
      let docs_response = await axios_instance({
        url: "/dts/getImageList",
        method: "POST",
        data: { reqData },
      });
      docs_response = decryptRequest(docs_response.data.resData);
      setDetailsState((prevState) => ({
        ...prevState,
        documentList: docs_response.documents,
      }));
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const fetchCaseData = async (loadPaymentSummary) => {
    try {
      if (loadPaymentSummary) {
        setPaymentSummaryLoading((prevState) => true);
      }
      setDetailFormLoading((prevState) => true);
      let reqData = encryptRequest({ lead_id });
      let casedetails = await axios_instance({
        url: "/dts/getDTSCaseDetail",
        method: "POST",
        data: { reqData },
      });

      casedetails = decryptRequest(casedetails.data.resData);

      setDetailsState((prevState) => ({
        ...prevState,
        caseDetails: {
          ...prevState.caseDetails,
          ...casedetails.result,
        },
      }));
      if (loadPaymentSummary) {
        setPaymentSummaryLoading((prevState) => false);
      }
      setDetailFormLoading((prevState) => false);
    } catch (error) {}
  };

  useEffect(() => {
    if (detailFormButtonClicked) {
      setShowLoadingModal(true);
      setDetailFormLoading(true);
    } else {
      setShowLoadingModal(false);
      setDetailFormLoading(false);
    }
  }, [detailFormButtonClicked]);

  const nodeRef = useRef(null);

  return (
    <DetailPageLoadingModalContext.Provider value={{ setShowLoadingModal }}>
      <span className="nav-md">
        {showLoadingModal && <LoadingModal />}

        <HoldCaseModal
          closeHoldCaseModal={() => {
            setDetailsState({ ...detailsState, showHoldCaseModal: false });
          }}
          showHoldCaseModal={detailsState.showHoldCaseModal}
          holdCaseReasonsArr={detailsState.holdCaseReasons}
          caseDetails={detailsState.caseDetails}
          user={detailsState.user}
          changeHoldStatus={changeHoldStatus}
        />

        {detailsState.showReleaseConfirmBox && (
          <ConfirmBox
            onClick={releaseCase}
            message="Are you sure you want to release case from hold status ?"
            closeModal={() =>
              setDetailsState((prevState) => ({
                ...prevState,
                showReleaseConfirmBox: false,
              }))
            }
          ></ConfirmBox>
        )}
        <style>{gallery_styles}</style>
        <ToastContainer
          closeOnClick={false}
          draggable={false}
          closeButton={false}
        />
        <Header
          isNavigationModal={detailsState.showNavigationModal}
          closeNavigationModal={() =>
            setDetailsState({ ...detailsState, showNavigationModal: false })
          }
          openNavigationModal={() =>
            setDetailsState({ ...detailsState, showNavigationModal: true })
          }
          isDetailPage={true}
          isLoading={isLoading}
          detailsState={detailsState}
          setDetailsState={setDetailsState}
          handleReleaseConfirm={handleReleaseConfirm}
        />
        <div className="clearfix"></div>
        <div className="container-fluid details-bottom-panel">
          <div className="row">
            <div className="main_wrapper">
              <div className="overlay" id="navigation-overly"></div>
              {detailsState.caseDetails.length > 0 ? (
                <HiddenFields
                  lead_id={lead_id}
                  caseDetails={detailsState.caseDetails}
                  user={detailsState.user}
                />
              ) : (
                ""
              )}

              <section className="right_wrapper">
                <div className="panel-white">
                  <div className="container-fluid"></div>

                  <div style={{ position: "relative", display: "-webkit-box" }}>
                    <div
                      className="cssdd1"
                      id="paneld"
                      style={{
                        background: "#F1F3F6",
                      }}
                    >
                      {detailsState.flip ? (
                        <div
                          id="flipd"
                          onClick={() =>
                            setDetailsState({ ...detailsState, flip: false })
                          }
                        >
                          <img
                            src={`${process.env.REACT_APP_STYLES_PATH}image.drawer.svg`}
                            className="img-shutter"
                            alt=""
                          />
                        </div>
                      ) : (
                        <CSSTransition
                          in={!detailsState.flip}
                          timeout={{ enter: 500, exit: 500 }}
                          classNames="slide"
                          unmountOnExit
                          nodeRef={nodeRef}
                        >
                          <div
                            className="paneldtl"
                            style={{
                              marginTop: "5px",
                            }}
                            ref={nodeRef}
                          >
                            <div className="row">
                              <div className="col-md-12 white-box">
                                <button
                                  type="button"
                                  className="close-btn style-close"
                                  onClick={() =>
                                    setDetailsState({
                                      ...detailsState,
                                      flip: true,
                                    })
                                  }
                                >
                                  <span aria-hidden="true">×</span>
                                </button>
                                <div className="row">
                                  {!isLoading && !paymentSummaryLoading ? (
                                    <PaymentSummary
                                      lead_id={lead_id}
                                      caseDetails={detailsState.caseDetails}
                                      fetchCaseData={fetchCaseData}
                                    />
                                  ) : (
                                    <div className="col-sm-4">
                                      <ContainerLoading />
                                    </div>
                                  )}

                                  {!isLoading ? (
                                    <DealerLedger
                                      caseDetails={detailsState.caseDetails}
                                      statuses={detailsState.statuses}
                                      substatuses={detailsState.substatuses}
                                      RCLimit={detailsState.RCLimit}
                                    />
                                  ) : (
                                    <div className="col-sm-4">
                                      <ContainerLoading />
                                    </div>
                                  )}
                                  {!isLoading &&
                                  Object.keys(detailsState.caseDetails)
                                    .length ? (
                                    <RTOSummary
                                      lead_id={lead_id}
                                      caseDetails={detailsState.caseDetails}
                                    />
                                  ) : (
                                    <div className="col-sm-4">
                                      <ContainerLoading />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </CSSTransition>
                      )}
                    </div>
                  </div>

                  <div className="clearfix">
                    <div className="navcontent-wrap clearfix details-bottom-panel">
                      {!isLoading &&
                      Object.keys(rbacConfig).length &&
                      detailsState.panels.length ? (
                        <DetailsSideBar
                          selectedPanel={detailsState.selectedPanel}
                          togglePanel={detailsState.togglePanel}
                          handlePanelClick={handlePanelClick}
                          stepSelected={detailsState.stepSelected}
                          handleStepClick={handleStepClick}
                          lead_id={lead_id}
                          sidePanelPermissions={
                            detailsState.caseDetails.sidePanelPermissions
                          }
                          changeMoneyReturnToBank={changeMoneyReturnToBank}
                          fetchCaseData={fetchPaymentDetails}
                          panels={detailsState.panels}
                          step_details={JSON.parse(
                            detailsState.caseDetails.step_details
                          )}
                          sub_stage={parseInt(
                            detailsState.caseDetails.sub_stage
                          )}
                        ></DetailsSideBar>
                      ) : (
                        <div className="navsidebarmenu">
                          <ContainerLoading />
                        </div>
                      )}

                      <div className="rightsidebox">
                        <div className="clearfix" style={{ display: "block" }}>
                          <div className="panel-body">
                            <div className="row">
                              <div className="">
                                <div className="row">
                                  <div className="tab-content">
                                    <div
                                      role="tabpanel"
                                      className="tab-pane fade in active"
                                      id="tab-particulars"
                                    >
                                      <div id="dt-docs-view">
                                        <div
                                          className="col-md-8"
                                          id="gallery-panel-html"
                                        >
                                          {detailsState.documentList.length &&
                                          !isLoading &&
                                          !imageLoading &&
                                          Object.keys(detailsState.caseDetails)
                                            .length &&
                                          rbacConfig.caseDetails ? (
                                            <div className="row">
                                              <div className="col-md-6 pad-R5">
                                                <ImageContainer
                                                  step={1}
                                                  id={1}
                                                  onDocumentChange={(e) =>
                                                    onDocumentChange(e, 0)
                                                  }
                                                  documents={
                                                    detailsState.documentList
                                                  }
                                                  docSelected={
                                                    detailsState.docType[0]
                                                  }
                                                  rotate={rotate}
                                                  rotationId={
                                                    detailsState.rotation1
                                                  }
                                                  prevDoc={prevDoc}
                                                  nextDoc={nextDoc}
                                                  canScroll={
                                                    detailsState.canScroll
                                                  }
                                                  enableScroll={enableScroll}
                                                  showScroll={true}
                                                  lead_id={lead_id}
                                                  dis_id={
                                                    detailsState.caseDetails.id
                                                  }
                                                  stepSelected={
                                                    detailsState.stepSelected
                                                  }
                                                ></ImageContainer>
                                              </div>
                                              <div className="col-md-6 pad-L10">
                                                <ImageContainer
                                                  step={1}
                                                  id={2}
                                                  onDocumentChange={(e) =>
                                                    onDocumentChange(e, 1)
                                                  }
                                                  documents={
                                                    detailsState.documentList
                                                  }
                                                  docSelected={
                                                    detailsState.docType[1]
                                                  }
                                                  rotate={rotate}
                                                  rotationId={
                                                    detailsState.rotation2
                                                  }
                                                  prevDoc={prevDoc}
                                                  nextDoc={nextDoc}
                                                  canScroll={
                                                    detailsState.canScroll
                                                  }
                                                  enableScroll={enableScroll}
                                                  showScroll={true}
                                                  lead_id={lead_id}
                                                  dis_id={
                                                    detailsState.caseDetails.id
                                                  }
                                                  sub_stage={
                                                    detailsState?.caseDetails
                                                      ?.sub_stage
                                                  }
                                                  dts_pay_count={
                                                    detailsState?.caseDetails
                                                      ?.dts_pay_count
                                                  }
                                                  is_reassigned={
                                                    detailsState?.caseDetails
                                                      ?.is_reassigned
                                                  }
                                                  stepSelected={
                                                    detailsState.stepSelected
                                                  }
                                                ></ImageContainer>
                                              </div>
                                            </div>
                                          ) : (
                                            <ContainerLoading
                                              galleryLoader={true}
                                            />
                                          )}
                                        </div>

                                        {!isLoading &&
                                        !detailFormLoading &&
                                        Object.keys(detailsState.caseDetails)
                                          .length &&
                                        rbacConfig.caseDetails &&
                                        Object.keys(rbacConfig.caseDetails)
                                          .length ? (
                                          <DetailFormContext.Provider
                                            value={{
                                              setDetailFormButtonClicked,
                                              panelTitle:
                                                detailsState?.stepObj
                                                  ?.panelTitle || "",
                                              stepTitle:
                                                detailsState?.stepObj?.title ||
                                                "",
                                            }}
                                          >
                                            <DetailForm
                                              caseDetails={
                                                rbacConfig.caseDetails
                                              }
                                              paymentdetails={
                                                detailsState.caseDetails
                                                  .paymentDetails
                                              }
                                              user={detailsState.user}
                                              selectedPanel={
                                                detailsState.selectedPanel
                                              }
                                              stepSelected={
                                                detailsState.stepSelected
                                              }
                                              stepObj={detailsState.stepObj}
                                              formFieldValues={rbacConfig}
                                              handleNextStep={handleNextStep}
                                              controlsystem={
                                                detailsState.controlsystem
                                              }
                                              payleadData={
                                                detailsState.payleadData
                                              }
                                              RCLimit={detailsState.RCLimit}
                                              fetchDetails={fetchPaymentDetails}
                                              isMoneyReturnToBank={
                                                isMoneyReturnToBank
                                              }
                                              changeMoneyReturnToBank={
                                                changeMoneyReturnToBank
                                              }
                                              fetchCaseData={fetchCaseData}
                                              fetchGallery={fetchGallery}
                                            />
                                          </DetailFormContext.Provider>
                                        ) : (
                                          <ContainerLoading
                                            galleryLoader={true}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="clearfix"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
        <div className="arrowTop">
          <div className="pageToup" style={{ display: "none" }}>
            <a href="/#" id="go-top">
              <span className="fa fa-arrow-up" aria-hidden="true"></span>
            </a>
          </div>
        </div>

        {Object.keys(detailsState.caseDetails).length ? (
          <RemarkPopper
            toggleRemarkPopper={toggleRemarkPopper}
            isRemarksOpen={detailsState.isRemarksOpen}
            disbursement_id={detailsState.caseDetails.id}
            lead_id={lead_id}
            user_type={detailsState.user.dts_user}
            fetchHoldCaseRemarksState={fetchHoldCaseRemarksState}
            setFetchHoldCaseRemarksState={setFetchHoldCaseRemarksState}
          ></RemarkPopper>
        ) : (
          ""
        )}
      </span>
    </DetailPageLoadingModalContext.Provider>
  );
};

export default Details;
